import axios from 'axios';
import { createApiRequest } from 'core/util';
import { constructApiUrl } from 'core/util/api';
import {
  COOKIE_CONSENT_DECLINED_AT_KEY,
  USER_CONTACT_URL,
  USER_URL
} from 'core/constants';
import store from 'core/store';
import type { AppThunk, AppDispatch } from 'core/store';
import {
  fetchUserFailure,
  setCookieConsent,
  setImpersonating,
  setUserId,
  setUserInfo,
  updateUserFailure,
  updateUserMeFailure,
  updateUserMeSuccess,
  updateUserSuccess
} from 'core/services/UserSlice';
import { setHomes } from 'core/services/HomeSlice';
import { requestHomeInfo } from 'core/services/HomeActions';

export const requestUserInfo = () =>
  createApiRequest({
    route: USER_URL,
    onGlobalSuccess: fetchGlobalSuccess,
    onSuccess: fetchUserSuccess,
    onError: fetchUserFailure,
    defaultErrorMessage: 'There was a problem retrieving your account.'
  })();

export const fetchUserSuccess =
  (me: any): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setUserId(me));
    dispatch(setUserInfo(me.contact));
    dispatch(setHomes(me.homes));
    dispatch(requestHomeInfo(me.homes[0].id));
  };

export const fetchGlobalSuccess =
  (global: any): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setImpersonating(global.impersonating));
  };

export const updateUserMe = (user: object) =>
  createApiRequest({
    route: USER_URL,
    method: 'patch',
    body: user,
    onSuccess: updateUserMeSuccess,
    onError: updateUserMeFailure,
    defaultErrorMessage: 'There was a problem updating your account.',
    useGlobalLoader: true
  })();

let cookieConsentCancelToken: ReturnType<
  typeof axios.CancelToken.source
> | null = null;

export const updateCookieConsent =
  (value: boolean): AppThunk =>
  async (dispatch: AppDispatch) => {
    const userState = store.getState().user;
    const previousValue = userState.cookieConsent;
    // Every path that changes cookie consent — the Account page toggle or the
    // consent modal's Accept/Decline — goes through here, so the decline
    // cooldown always starts from the moment the user actually made the
    // choice. A direct decline() writing this timestamp on its own (the prior
    // approach) left the toggle able to flip consent without ever starting
    // the cooldown, so the auto-gate modal treated it as never-declined and
    // re-prompted immediately.
    //
    // Scoped to userId: a bare timestamp would survive a logout, so a
    // different account signing in on the same device could inherit this
    // account's cooldown and have its own required prompt suppressed.
    if (value) {
      localStorage.removeItem(COOKIE_CONSENT_DECLINED_AT_KEY);
    } else {
      localStorage.setItem(
        COOKIE_CONSENT_DECLINED_AT_KEY,
        JSON.stringify({ userId: userState.id, declinedAt: Date.now() })
      );
    }
    // Flip the ON/OFF status immediately so the toggle doesn't feel unresponsive
    // while the PATCH is in flight; roll back to previousValue if it fails.
    dispatch(setCookieConsent(value));
    if (cookieConsentCancelToken) {
      cookieConsentCancelToken.cancel();
    }
    cookieConsentCancelToken = axios.CancelToken.source();
    const cancelToken = cookieConsentCancelToken.token;
    const authToken = store.getState().login.accessToken;
    try {
      const url = constructApiUrl({ route: USER_URL });
      const response = await axios.patch(
        url,
        { cookie_consent: value },
        {
          headers: { Authorization: authToken ? `Bearer ${authToken}` : '' },
          cancelToken
        }
      );
      // A logout/login while this PATCH was in flight would otherwise let a
      // late response overwrite the new session's cookieConsent with a value
      // that belongs to whoever was signed in when the request was sent.
      if (store.getState().login.accessToken !== authToken) return;
      dispatch(updateUserMeSuccess(response.data.results || response.data));
    } catch (e) {
      if (
        !axios.isCancel(e) &&
        store.getState().login.accessToken === authToken
      ) {
        // Roll back the displayed ON/OFF value since the server never
        // confirmed it, but deliberately leave the decline-cooldown
        // timestamp in place: it reflects the user's local choice a moment
        // ago, and undoing it here would let the auto-gate modal re-prompt
        // immediately just because persistence failed — a confusing loop
        // that's independent of what the user actually decided.
        dispatch(setCookieConsent(previousValue));
        dispatch(
          updateUserMeFailure(
            'There was a problem updating your cookie settings.'
          )
        );
      }
    }
  };

export const updateUser = (user: object) =>
  createApiRequest({
    route: USER_CONTACT_URL,
    method: 'patch',
    body: user,
    onSuccess: updateUserSuccess,
    onError: updateUserFailure,
    defaultErrorMessage: 'There was a problem updating your account.',
    useGlobalLoader: true
  })();
